// =============================================================================
// Optim TURPE V2 : l'écriture des montants signés (revue du 08/10, retour R1).
//
// Ce que le client doit vivre : un montant se lit partout de la même façon. Le
// signe dit l'effet pour lui : « + » il économise, « − » il paie plus ; 0 reste
// « 0 € », sans signe. Le vert accompagne le « + », le rouge le « − », jamais
// seuls. Une grandeur sans sens (l'écart entre deux scénarios, des dépassements
// estimés) s'écrit sans signe et porte un mot dans sa phrase.
//
// Cette écriture est la seule de la page : le titre, les cartes des scénarios,
// « D'où vient ce montant », la comparaison avec l'ancienne version,
// l'historique, le résumé à copier, le texte de la demande et les phrases de
// recommandation la prennent ici.
// =============================================================================

import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"

/** Le signe moins typographique (U+2212), jamais le trait d'union. */
export const SIGNE_MOINS = "−"

/** L'effet d'un montant pour le client, une fois arrondi à l'euro affiché. */
export type SensMontant = "gain" | "perte" | "nul"

const entierFr = formateurFr({ maximumFractionDigits: 0 })

function fini(valeur: unknown): valeur is number {
    return typeof valeur === "number" && Number.isFinite(valeur)
}

/**
 * Le sens d'un montant tel qu'il s'affiche : 0,4 € s'écrit « 0 € », il est donc
 * nul, sans signe ni couleur.
 */
export function sensMontant(valeur: number): SensMontant {
    const arrondi = Math.round(Math.abs(valeur))
    if (arrondi === 0) return "nul"
    return valeur > 0 ? "gain" : "perte"
}

/** « + », « − » ou rien pour un montant nul. */
export function signeMontant(valeur: number): string {
    const sens = sensMontant(valeur)
    return sens === "gain" ? "+" : sens === "perte" ? SIGNE_MOINS : ""
}

/** « 1 583 », sans signe : la valeur absolue arrondie à l'euro. */
export function nombreEuros(valeur: number): string {
    return entierFr.format(Math.round(Math.abs(valeur)))
}

/**
 * Le nombre d'une dépense de dépassement, sans signe (décision du pilote du
 * 14/10/2026) : sous 0,50 €, elle s'écrit « moins de 1 », jamais « 0 » (un
 * montant non nul ne s'écrit jamais 0) ; sinon comme `nombreEuros`. Un zéro
 * servi reste « 0 ».
 */
export function nombreEurosDepassement(valeur: number): string {
    const absolu = Math.abs(valeur)
    if (absolu > 0 && absolu < 0.5) return "moins de 1"
    return nombreEuros(valeur)
}

/** « € » ou « € HT » : le régime s'écrit après l'euro, quel que soit son libellé. */
function suiteEuros(regime: string | null): string {
    const reste = (regime ?? "").replace(/^€\s*/, "").trim()
    return reste ? `\u00a0€\u00a0${reste}` : "\u00a0€"
}

/**
 * « +1 583 € », « −84 € », « 0 € » ; avec un régime, « +1 583 € HT ».
 * `null` pour un montant absent : jamais un 0.
 */
export function formatMontantSigne(
    valeur: number | null | undefined,
    regime: string | null = null
): string | null {
    if (!fini(valeur)) return null
    const suite = suiteEuros(regime)
    return `${signeMontant(valeur)}${nombreEuros(valeur)}${suite}`
}

/**
 * Le mot qui suit un montant de tête (revue du support du 08/10, A2) : « +632 € HT
 * d'économie sur 365 jours », « −246 € HT de surcoût sur 365 jours ». Le signe
 * seul ne se lisait pas (« est-ce que c'est +632 € d'économies ? ») ; un montant
 * nul n'a pas de mot. Le mot suit le sens AFFICHÉ (arrondi à l'euro).
 */
export function motDuMontant(valeur: number | null | undefined): string | null {
    if (!fini(valeur)) return null
    const sens = sensMontant(valeur)
    return sens === "gain"
        ? "d'économie"
        : sens === "perte"
          ? "de surcoût"
          : null
}

/** Le chiffre seul, signé, sans unité (« +647 »), pour les cartes. */
export function formatNombreSigne(
    valeur: number | null | undefined
): string | null {
    if (!fini(valeur)) return null
    return `${signeMontant(valeur)}${nombreEuros(valeur)}`
}

/**
 * « 1 199 € », sans signe : une grandeur sans sens pour le client (écart entre
 * deux scénarios, dépassements estimés), qui porte son mot dans la phrase.
 */
export function formatMontantSansSigne(
    valeur: number | null | undefined,
    regime: string | null = null
): string | null {
    if (!fini(valeur)) return null
    const suite = suiteEuros(regime)
    return `${nombreEuros(valeur)}${suite}`
}

/** Les classes de couleur déjà employées par le module. */
export const CLASSE_GAIN = "text-[var(--p-green-700)]"
export const CLASSE_PERTE = "text-[var(--p-red-700)]"

/**
 * La couleur d'un montant signé : vert pour un gain, rouge pour une perte, la
 * classe neutre de l'appelant pour 0 ou une absence.
 */
export function classeMontant(
    valeur: number | null | undefined,
    neutre = ""
): string {
    if (!fini(valeur)) return neutre
    const sens = sensMontant(valeur)
    return sens === "gain"
        ? CLASSE_GAIN
        : sens === "perte"
          ? CLASSE_PERTE
          : neutre
}

/** Un morceau de texte, signé ou non. */
export type SegmentMontant = { texte: string; sens: SensMontant | null }

/**
 * Un montant signé écrit par ce module : « + » ou « − » collé au chiffre,
 * suivi de « € ». Le trait d'union n'est jamais pris pour un signe (dates,
 * mots composés).
 */
const MONTANT_SIGNE = /[+−]\d[\d\u202f\u00a0 ]*(?:,\d+)?\s?€/g

/**
 * Découpe un texte d'en-tête en morceaux, pour colorer ses montants signés sans
 * toucher au reste : « part fixe +563 €, consommation 0 € » donne un morceau
 * neutre, un morceau « gain », puis un morceau neutre.
 */
export function segmenterMontants(
    texte: string | null | undefined
): SegmentMontant[] {
    if (!texte) return []
    const segments: SegmentMontant[] = []
    let dernier = 0
    for (const m of texte.matchAll(MONTANT_SIGNE)) {
        const debut = m.index ?? 0
        if (debut > dernier)
            segments.push({ texte: texte.slice(dernier, debut), sens: null })
        segments.push({
            texte: m[0],
            sens: m[0].startsWith("+") ? "gain" : "perte",
        })
        dernier = debut + m[0].length
    }
    if (dernier < texte.length)
        segments.push({ texte: texte.slice(dernier), sens: null })
    return segments
}

/** La classe d'un morceau signé. */
export function classeSegment(sens: SensMontant | null): string {
    return sens === "gain" ? CLASSE_GAIN : sens === "perte" ? CLASSE_PERTE : ""
}

/**
 * N3 (revue du 19/10/2026) : la suite d'un KPI en grand, collée au nombre.
 * « € HT net par an » ne se coupe jamais (espaces insécables) et part avec le
 * nombre ; une période longue (« sur la période du 01/03/2025 au 28/02/2026 »)
 * garde son premier mot collé à l'unité puis peut passer à la ligne, pour ne
 * jamais déborder d'une carte étroite. L'espace insécable de tête sépare le
 * nombre de sa suite sans permettre de coupure entre eux.
 */
export function suiteColleeAuNombre(
    unite: string | null,
    periode: string | null
): string {
    const insecable = (s: string) => s.trim().replace(/\s+/g, "\u00a0")
    const tete = insecable(unite ?? "")
    const queue = (periode ?? "").trim()
    if (!queue) return tete ? `\u00a0${tete}` : ""
    const [premier, ...reste] =
        queue === "par an" ? [insecable(queue)] : queue.split(/\s+/)
    return [
        `\u00a0${[tete, premier].filter(Boolean).join("\u00a0")}`,
        ...reste,
    ].join(" ")
}
